import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Grid, Button,
  Table, TableBody, TableRow, TableCell, TableHead, Avatar, alpha, useTheme,
  FormControl, Select, MenuItem, InputLabel, Checkbox, ListItemText, OutlinedInput
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MapIcon from '@mui/icons-material/Map';
import PersonIcon from '@mui/icons-material/Person';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HistoryIcon from '@mui/icons-material/History';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { searchElectorates, searchParliamentarians, searchElectorateMaps, getElectorateMapGeoJson, getElectorateElections } from '../services/api';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import { getParliamentarianPhotoUrl } from '../utils/photos';
import CopyLinkButton from './CopyLinkButton';
import ShareButton from './ShareButton';
import { basemapFor } from '../theme';

// Component to fit map bounds to multiple GeoJSON layers
const FitBoundsMultiple = ({ geoJsonMap }) => {
  const map = useMap();

  useEffect(() => {
    if (!geoJsonMap || Object.keys(geoJsonMap).length === 0) return;

    try {
      const allBounds = [];
      Object.values(geoJsonMap).forEach(geoJson => {
        if (geoJson && geoJson.features && geoJson.features.length > 0) {
          const layer = L.geoJSON(geoJson);
          const bounds = layer.getBounds();
          if (bounds.isValid()) {
            allBounds.push(bounds);
          }
        }
      });

      if (allBounds.length > 0) {
        let combinedBounds = allBounds[0];
        for (let i = 1; i < allBounds.length; i++) {
          combinedBounds = combinedBounds.extend(allBounds[i]);
        }
        map.fitBounds(combinedBounds, { padding: [30, 30] });
      }
    } catch (err) {
      console.error('Error fitting bounds:', err);
    }
  }, [geoJsonMap, map]);

  return null;
};

// Color palette for overlaying multiple boundaries
const BOUNDARY_COLORS = [
  { color: '#1565c0', fill: '#1976d2' },  // Blue (current/most recent)
  { color: '#c62828', fill: '#e53935' },  // Red
  { color: '#2e7d32', fill: '#43a047' },  // Green
  { color: '#7b1fa2', fill: '#9c27b0' },  // Purple
  { color: '#ef6c00', fill: '#fb8c00' },  // Orange
  { color: '#00838f', fill: '#00acc1' },  // Cyan
  { color: '#6a1b9a', fill: '#8e24aa' },  // Deep Purple
  { color: '#d84315', fill: '#f4511e' },  // Deep Orange
  { color: '#1b5e20', fill: '#388e3c' },  // Dark Green
  { color: '#0d47a1', fill: '#1976d2' },  // Dark Blue
];

export default function ElectorateDetail() {
  const { eid } = useParams();
  const theme = useTheme();
  const [electorate, setElectorate] = useState(null);
  const [electionResults, setElectionResults] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapBoundaries, setMapBoundaries] = useState([]);
  const [selectedMapIds, setSelectedMapIds] = useState([]);
  const [geoJsonDataMap, setGeoJsonDataMap] = useState({});
  const [mapLoading, setMapLoading] = useState(false);

  // Election results for this seat (#electorate elections). Best-effort.
  useEffect(() => {
    let active = true;
    setElectionResults([]);
    (async () => {
      try {
        const res = await getElectorateElections(eid);
        if (active) setElectionResults(res.elections || []);
      } catch (e) {
        if (active) setElectionResults([]);
      }
    })();
    return () => { active = false; };
  }, [eid]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch electorate by EID
        const electorateData = await searchElectorates(`EID:${eid}`, 1, 1);
        if (electorateData.hits && electorateData.hits.length > 0) {
          const elec = electorateData.hits[0];
          setElectorate(elec);

          // Fetch members who served in this electorate.
          // Parliamentarian records carry no electorate ID, only the
          // electorate *name*. Several distinct electorates share a name
          // (e.g. Fraser existed in the ACT, ceased 2016, and now exists
          // in Victoria). Disambiguate by also matching State so members
          // from a like-named electorate in another state/territory don't
          // bleed in (issue #104).
          try {
            const elecState = elec.State;
            const memberQuery = elecState
              ? `Electorate:"${elec.Electorate}" AND State:"${elecState}"`
              : `Electorate:"${elec.Electorate}"`;
            const membersData = await searchParliamentarians(memberQuery, 1, 100);
            // Sort by service start date (most recent first)
            const sortedMembers = (membersData.hits || [])
              .filter(m => !elecState || m.State === elecState)
              .sort((a, b) => {
                const aStart = a.ServiceHistory_Start ? new Date(a.ServiceHistory_Start) : new Date(0);
                const bStart = b.ServiceHistory_Start ? new Date(b.ServiceHistory_Start) : new Date(0);
                return bStart - aStart;
              });
            setMembers(sortedMembers);
          } catch (memberErr) {
            console.error('Error fetching members:', memberErr);
          }

          // Fetch electorate map boundaries
          try {
            const mapsData = await searchElectorateMaps(`eid:${eid}`, 1, 100, null, eid);
            if (mapsData.hits && mapsData.hits.length > 0) {
              // Sort by date_from descending (most recent first)
              const sortedMaps = mapsData.hits.sort((a, b) => (b.date_from || 0) - (a.date_from || 0));
              setMapBoundaries(sortedMaps);
              // Select the most recent (current) boundary by default
              const currentMap = sortedMaps.find(m => m.is_current) || sortedMaps[0];
              if (currentMap) {
                setSelectedMapIds([currentMap.id]);
              }
            }
          } catch (mapErr) {
            console.error('Error fetching map boundaries:', mapErr);
          }
        } else {
          setError('Electorate not found');
        }
      } catch (err) {
        console.error('Error fetching electorate:', err);
        setError('Failed to load electorate data');
      }
      setLoading(false);
    };
    fetchData();
  }, [eid]);

  // Fetch GeoJSON data when selected maps change
  useEffect(() => {
    // Use ref to track if component is mounted
    let isMounted = true;

    const fetchGeoJsonData = async () => {
      if (!selectedMapIds || selectedMapIds.length === 0) {
        setGeoJsonDataMap({});
        return;
      }

      setMapLoading(true);
      const newGeoJsonMap = {};

      // Fetch GeoJSON for each selected map ID (server-side conversion)
      await Promise.all(
        selectedMapIds.map(async (mapId) => {
          // Skip if already cached
          if (geoJsonDataMap[mapId]) {
            newGeoJsonMap[mapId] = geoJsonDataMap[mapId];
            return;
          }
          try {
            // Use server-side GeoJSON with simplification for faster loading
            const geoJson = await getElectorateMapGeoJson(mapId, 0.001);
            newGeoJsonMap[mapId] = geoJson;
          } catch (err) {
            console.error(`Error fetching GeoJSON for ${mapId}:`, err);
          }
        })
      );

      // Only update state if component is still mounted
      if (isMounted) {
        setGeoJsonDataMap(newGeoJsonMap);
        setMapLoading(false);
      }
    };

    fetchGeoJsonData();

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMapIds]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getYearFromDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).getFullYear();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !electorate) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Electorate not found'}</Typography>
        <Button component={RouterLink} to="/electorates" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Electorates
        </Button>
      </Box>
    );
  }

  // Determine status color
  const isCurrent = !electorate.Ceased;
  const statusColor = isCurrent ? '#2e7d32' : '#757575';

  // Calculate years active
  const establishedYear = getYearFromDate(electorate.Established);
  const ceasedYear = getYearFromDate(electorate.Ceased);
  const yearsActive = ceasedYear && establishedYear
    ? ceasedYear - establishedYear
    : establishedYear
      ? new Date().getFullYear() - establishedYear
      : null;

  // Find current member.
  // A ceased electorate (e.g. the ACT's Fraser, abolished 2016) can never
  // have a current member, even though a like-named electorate elsewhere
  // (Victoria's Fraser) does. Only surface a current member when this
  // electorate is itself current AND the member's State matches it, so a
  // sitting member of a same-named electorate in another state/territory
  // is not shown here (issue #104).
  const currentMember = isCurrent
    ? members.find(m =>
        m.InCurrentParliament === 'True' &&
        m.Electorate === electorate.Electorate &&
        (!electorate.State || m.State === electorate.State)
      )
    : null;

  return (
    <Box>
      {/* Navigation bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          component={RouterLink}
          to="/electorates"
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to Electorates
        </Button>
        <CopyLinkButton size="small" />
        <ShareButton size="small" />
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12}>
          {/* Hero Section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(statusColor, 0.08)} 0%, ${alpha(statusColor, 0.02)} 100%)`,
              border: '1px solid',
              borderColor: alpha(statusColor, 0.15)
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <MapIcon sx={{ fontSize: 48, color: statusColor }} />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: statusColor,
                    mb: 0.5
                  }}
                >
                  {electorate.Electorate}
                </Typography>

                {/* Location subtitle */}
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 400 }}>
                  {electorate.State}
                </Typography>

                {/* Status chips */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Chip
                    label={electorate.State}
                    size="small"
                    sx={{
                      bgcolor: '#2e7d32',
                      color: 'white',
                      fontWeight: 500
                    }}
                  />
                  <Chip
                    label="House of Representatives"
                    size="small"
                    variant="outlined"
                  />
                  {isCurrent ? (
                    <Chip
                      label="Current"
                      size="small"
                      color="success"
                    />
                  ) : (
                    <Chip
                      label="Historical"
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  )}
                  {yearsActive && (
                    <Chip
                      label={`${yearsActive} years`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>

                {/* Name origin text rendered without a "Name origin:" label
                    per user feedback 2026-06-01 — the Origin field typically
                    starts with "Named after …" / "Named for …" so the prefix
                    was redundant. */}
                {electorate.Origin && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {electorate.Origin}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Current Member Section */}
          {currentMember && (
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="primary" />
                Current Member
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={getParliamentarianPhotoUrl(currentMember.PHID)}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: '#1565c0',
                    border: '3px solid white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}
                >
                  {currentMember.GivenName?.[0]}{currentMember.FamilyName?.[0]}
                </Avatar>
                <Box>
                  <Button
                    component={RouterLink}
                    to={`/parliamentarians/${currentMember.PHID}`}
                    sx={{ p: 0, textTransform: 'none', fontWeight: 600, fontSize: '1.1rem' }}
                  >
                    {currentMember.DisplayName || `${currentMember.GivenName} ${currentMember.FamilyName}`}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {currentMember.Party}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Since {formatDate(currentMember.ServiceHistory_Start)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Electorate Map Section */}
          {mapBoundaries.length > 0 && (
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MapIcon color="primary" />
                  Electorate Boundary
                </Typography>
                {mapBoundaries.length > 1 && (
                  <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel id="map-period-label">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <HistoryIcon fontSize="small" />
                        Select Boundaries to Compare
                      </Box>
                    </InputLabel>
                    <Select
                      labelId="map-period-label"
                      multiple
                      value={selectedMapIds}
                      label="Select Boundaries to Compare"
                      onChange={(e) => setSelectedMapIds(e.target.value)}
                      input={<OutlinedInput label="Select Boundaries to Compare" />}
                      renderValue={(selected) => {
                        if (selected.length === 0) return '';
                        if (selected.length === 1) {
                          const map = mapBoundaries.find(m => m.id === selected[0]);
                          return map?.period_label || '';
                        }
                        return `${selected.length} boundaries selected`;
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: { maxHeight: 400 }
                        }
                      }}
                    >
                      {mapBoundaries.map((map) => (
                        <MenuItem key={map.id} value={map.id}>
                          <Checkbox checked={selectedMapIds.includes(map.id)} />
                          <ListItemText
                            primary={map.period_label + (map.is_current ? ' (Current)' : '')}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>

              {/* Map Container */}
              <Box
                sx={{
                  width: '100%',
                  height: 450,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  position: 'relative',
                  bgcolor: '#f5f5f5'
                }}
              >
                {mapLoading && (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: (t) => alpha(t.palette.background.paper, 0.8),
                    zIndex: 1000
                  }}>
                    <CircularProgress />
                  </Box>
                )}
                {Object.keys(geoJsonDataMap).length > 0 ? (
                  <MapContainer
                    center={[-25.0, 133.0]}
                    zoom={4}
                    style={{ height: '100%', width: '100%' }}
                    key={selectedMapIds.join('-')}
                  >
                    <TileLayer
                      attribution={basemapFor(theme.palette.mode).attribution}
                      url={basemapFor(theme.palette.mode).url}
                      subdomains={basemapFor(theme.palette.mode).subdomains || 'abc'}
                    />
                    {/* Render each selected boundary with a different color */}
                    {selectedMapIds.map((mapId, index) => {
                      const geoJson = geoJsonDataMap[mapId];
                      if (!geoJson || !geoJson.features || geoJson.features.length === 0) return null;
                      const colorIndex = index % BOUNDARY_COLORS.length;
                      const colors = BOUNDARY_COLORS[colorIndex];
                      return (
                        <GeoJSON
                          key={mapId}
                          data={geoJson}
                          style={{
                            color: colors.color,
                            weight: 2,
                            fillColor: colors.fill,
                            fillOpacity: selectedMapIds.length > 1 ? 0.2 : 0.3
                          }}
                        />
                      );
                    })}
                    <FitBoundsMultiple geoJsonMap={geoJsonDataMap} />
                  </MapContainer>
                ) : !mapLoading && (
                  <Box sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 1
                  }}>
                    <MapIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {selectedMapIds.length === 0 ? 'Select a boundary to display' : 'Map boundary data unavailable'}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Legend for selected boundaries */}
              {selectedMapIds.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {selectedMapIds.length === 1 ? 'Selected boundary:' : 'Selected boundaries (overlaid):'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {selectedMapIds.map((mapId, index) => {
                      const map = mapBoundaries.find(m => m.id === mapId);
                      if (!map) return null;
                      const colorIndex = index % BOUNDARY_COLORS.length;
                      const colors = BOUNDARY_COLORS[colorIndex];
                      return (
                        <Chip
                          key={mapId}
                          label={map.period_label + (map.is_current ? ' (Current)' : '')}
                          size="small"
                          onDelete={() => setSelectedMapIds(selectedMapIds.filter(id => id !== mapId))}
                          sx={{
                            bgcolor: alpha(colors.fill, 0.2),
                            borderColor: colors.color,
                            border: '1px solid',
                            '& .MuiChip-deleteIcon': {
                              color: colors.color,
                              '&:hover': { color: colors.fill }
                            }
                          }}
                        />
                      );
                    })}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({mapBoundaries.length} version{mapBoundaries.length !== 1 ? 's' : ''} available)
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* External links */}
              {isCurrent && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                  <Button
                    href={`https://www.aec.gov.au/profiles/${electorate.StateAbbrev?.toLowerCase() || electorate.State?.substring(0,3).toLowerCase()}/${electorate.Electorate.toLowerCase().replace(/['']/g, '').replace(/\s+/g, '-')}.htm`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                  >
                    View AEC Profile
                  </Button>
                </Box>
              )}
            </Paper>
          )}

          <Grid container spacing={3}>
            {/* Electorate Details */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Electorate Details
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary', width: '40%' }}>Electorate ID</TableCell>
                      <TableCell>{electorate.EID}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>State</TableCell>
                      <TableCell>{electorate.State}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Established</TableCell>
                      <TableCell>{formatDate(electorate.Established)}</TableCell>
                    </TableRow>
                    {electorate.Ceased && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Ceased</TableCell>
                        <TableCell>{formatDate(electorate.Ceased)}</TableCell>
                      </TableRow>
                    )}
                    {yearsActive && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Duration</TableCell>
                        <TableCell>{yearsActive} years</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            {/* Statistics */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Statistics
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary', width: '40%' }}>Total Members</TableCell>
                      <TableCell>{members.length}</TableCell>
                    </TableRow>
                    {currentMember && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Current Member</TableCell>
                        <TableCell>Yes</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Chamber</TableCell>
                      <TableCell>House of Representatives</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            {/* Notes */}
            {electorate.Notes && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Notes
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {electorate.Notes}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Members History */}
            {members.length > 0 && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Members of Parliament ({members.length})
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Party</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Service Start</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Service End</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {members.map((member) => (
                        <TableRow
                          key={member.PHID}
                          sx={{
                            bgcolor: member.InCurrentParliament === 'True' ? alpha(theme.palette.success.main, 0.05) : 'inherit'
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                src={getParliamentarianPhotoUrl(member.PHID)}
                                sx={{ width: 32, height: 32, fontSize: '0.75rem' }}
                              >
                                {member.GivenName?.[0]}{member.FamilyName?.[0]}
                              </Avatar>
                              <Button
                                component={RouterLink}
                                to={`/parliamentarians/${member.PHID}`}
                                size="small"
                                sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                              >
                                {member.DisplayName || `${member.GivenName} ${member.FamilyName}`}
                              </Button>
                              {member.InCurrentParliament === 'True' && (
                                <Chip label="Current" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {member.Party}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(member.ServiceHistory_Start)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {member.InCurrentParliament === 'True' ? 'Present' : formatDate(member.ServiceHistory_End)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {member.ServiceHistory_Duration?.split(' ').slice(0, 2).join(' ') || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            )}

          </Grid>
        </Grid>
      </Grid>

      {/* Election results for this seat — full table, no internal scroll. */}
      {electionResults.length > 0 && (
        <Paper elevation={1} sx={{ p: { xs: 2, md: 3 }, mt: 3, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HowToVoteIcon color="primary" /> Election results
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Every general election in {electorate?.Electorate} — winner, two-party-preferred
            result, swing and turnout. Select a year for the full result; the winner links to
            their profile.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Year', 'Member elected', 'Party', '2CP %', 'Swing', 'Turnout %', 'Candidates'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {electionResults.map((e, i) => (
                <TableRow key={`${e.Year}-${i}`} hover>
                  <TableCell>
                    {e.election_id ? (
                      <RouterLink to={`/elections/${e.election_id}`}
                        style={{ color: theme.palette.ledger.accentText, textDecoration: 'none' }}>
                        {e.Year}
                      </RouterLink>
                    ) : e.Year}
                  </TableCell>
                  <TableCell>
                    {e.Phid ? (
                      <RouterLink to={`/parliamentarians/${e.Phid}`}
                        style={{ color: theme.palette.ledger.accentText, textDecoration: 'none' }}>
                        {e.Winner}
                      </RouterLink>
                    ) : (e.Winner || '—')}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {e.PartyColour && (
                        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: e.PartyColour, flexShrink: 0 }} />
                      )}
                      {e.Party || '—'}
                    </Box>
                  </TableCell>
                  <TableCell>{e.TCP_pct == null ? '—' : `${e.TCP_pct}%`}</TableCell>
                  <TableCell>{e.Swing == null ? '—' : `${e.Swing > 0 ? '+' : ''}${e.Swing}`}</TableCell>
                  <TableCell>{e.Turnout == null ? '—' : `${e.Turnout}%`}</TableCell>
                  <TableCell>{e.candidate_count || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
